import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronUp, Monitor } from 'lucide-react';
import type { UseInchSimulationResult } from '../hooks/useInchSimulation';
import type { AspectRatioId } from '../utils/inchSimulation';
import {
  CUSTOM_SIZE_MAX_CM,
  CUSTOM_SIZE_MIN_CM,
  parseCustomCmInput,
  RESOLUTION_SLIDER_MAX,
  RESOLUTION_SLIDER_MIN,
  RESOLUTION_SLIDER_STEP,
} from '../utils/inchSimulation';
import { SimulatedViewportFrame } from './SimulatedViewportFrame';
import styles from '../pages/DisplayPage.module.css';

export { SimulationFrameContext, useSimulationFrame } from '../context/SimulationFrameContext';

export const DisplayChromePortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className={styles.displayChromePortal}>
      {children}
    </div>,
    document.body
  );
};

function cmFieldValue(cm: number | null): string {
  return cm == null ? '' : String(cm);
}

interface InchSimulationControlProps {
  simulation: UseInchSimulationResult;
}

export const InchSimulationControl: React.FC<InchSimulationControlProps> = ({
  simulation,
}) => {
  const {
    nativeInches,
    selectedInches,
    isSimulating,
    hasPartialCustomSize,
    options,
    resolutionOptions,
    aspectOptions,
    sliderWidth,
    customWidthCm,
    customHeightCm,
    simulatedScreen,
    selectedAspectId,
    controlsOpen,
    setSelectedInches,
    setSelectedWidthPx,
    setSelectedAspectId,
    setCustomWidthCm,
    setCustomHeightCm,
    setControlsOpen,
    resetToNative,
  } = simulation;

  const nativeLabel = Number.isInteger(nativeInches)
    ? `${nativeInches}"`
    : `${nativeInches.toFixed(1)}"`;
  const inchesDisabled = hasPartialCustomSize;

  const summary = simulation.isCustomSizeActive
    && simulation.customWidthCm != null
    && simulation.customHeightCm != null
    ? `${simulation.customWidthCm}×${simulation.customHeightCm} cm · ${simulatedScreen.widthPx}×${simulatedScreen.heightPx}`
    : isSimulating
      ? `${selectedInches != null ? `${selectedInches}"` : `~${nativeLabel}`} · ${selectedAspectId} · ${simulatedScreen.widthPx}×${simulatedScreen.heightPx}`
      : 'Monitor atual';

  return (
    <div>
      <button
        type="button"
        onClick={() => setControlsOpen(!controlsOpen)}
        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <span className="text-sm font-medium text-gray-700 flex items-center gap-1.5 min-w-0">
          <Monitor size={16} className="text-gray-500 shrink-0" />
          <span className="truncate">Simulação</span>
        </span>
        <span className="flex items-center gap-1.5 shrink-0 ml-2">
          {!controlsOpen && (
            <span className="text-xs text-gray-500 max-w-[9.5rem] truncate">{summary}</span>
          )}
          {controlsOpen ? (
            <ChevronUp size={18} className="text-gray-500" />
          ) : (
            <ChevronDown size={18} className="text-gray-500" />
          )}
        </span>
      </button>

      {controlsOpen && (
      <div className="mt-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className="block text-xs text-gray-500 mb-1">Polegada</span>
          <select
            value={selectedInches == null ? 'native' : String(selectedInches)}
            disabled={inchesDisabled}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedInches(value === 'native' ? null : Number(value));
            }}
            className={`w-full p-2 border border-gray-300 rounded-lg bg-white text-sm ${
              inchesDisabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''
            }`}
          >
            <option value="native">~{nativeLabel}</option>
            {options.map((inches) => (
              <option key={inches} value={inches}>
                {inches}"
              </option>
            ))}
          </select>
        </div>
        <div>
          <span className="block text-xs text-gray-500 mb-1">Proporção</span>
          <select
            value={selectedAspectId}
            disabled={inchesDisabled}
            onChange={(e) => setSelectedAspectId(e.target.value as AspectRatioId)}
            className={`w-full p-2 border border-gray-300 rounded-lg bg-white text-sm ${
              inchesDisabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''
            }`}
          >
            {aspectOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-2">
        <span className="block text-xs text-gray-500 mb-1">Medida (cm)</span>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="number"
            inputMode="decimal"
            min={CUSTOM_SIZE_MIN_CM}
            max={CUSTOM_SIZE_MAX_CM}
            step={0.1}
            placeholder="Largura"
            value={cmFieldValue(customWidthCm)}
            onChange={(e) => setCustomWidthCm(parseCustomCmInput(e.target.value))}
            className="w-full p-2 border border-gray-300 rounded-lg bg-white text-sm"
          />
          <input
            type="number"
            inputMode="decimal"
            min={CUSTOM_SIZE_MIN_CM}
            max={CUSTOM_SIZE_MAX_CM}
            step={0.1}
            placeholder="Altura"
            value={cmFieldValue(customHeightCm)}
            onChange={(e) => setCustomHeightCm(parseCustomCmInput(e.target.value))}
            className="w-full p-2 border border-gray-300 rounded-lg bg-white text-sm"
          />
        </div>
      </div>
      <div className="mt-2">
        <span className="block text-xs text-gray-500 mb-1">
          Resolução {simulatedScreen.widthPx}×{simulatedScreen.heightPx}
        </span>
        <input
          type="range"
          min={RESOLUTION_SLIDER_MIN}
          max={RESOLUTION_SLIDER_MAX}
          step={RESOLUTION_SLIDER_STEP}
          value={sliderWidth}
          onChange={(e) => setSelectedWidthPx(Number(e.target.value))}
          className="w-full accent-blue-500"
        />
        <div className="flex justify-between gap-1 mt-1">
          {resolutionOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setSelectedWidthPx(option.width)}
              className={`text-[11px] px-1 py-0.5 rounded ${
                sliderWidth === option.width
                  ? 'text-blue-600 font-semibold'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      {isSimulating && (
        <button
          type="button"
          onClick={resetToNative}
          className="mt-2 w-full text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          Monitor atual
        </button>
      )}
      </div>
      )}
    </div>
  );
};

interface InchSimulationBadgeProps {
  simulation: UseInchSimulationResult;
}

export const InchSimulationBadge: React.FC<InchSimulationBadgeProps> = ({
  simulation,
}) => {
  if (!simulation.isSimulating) return null;

  const sizeLabel = simulation.isCustomSizeActive
    && simulation.customWidthCm != null
    && simulation.customHeightCm != null
    ? `${simulation.customWidthCm}×${simulation.customHeightCm} cm`
    : simulation.selectedInches != null
      ? `${simulation.selectedInches}"`
      : `~${simulation.nativeInches.toFixed(1)}"`;

  return (
    <div
      className="fixed bottom-4 left-4 z-[90] pointer-events-none select-none"
      aria-live="polite"
    >
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-black/70 text-white text-sm backdrop-blur-sm border border-white/20">
        <Monitor size={14} />
        <span>
          {sizeLabel} · {simulation.cssWidth}×{simulation.cssHeight}
        </span>
      </div>
    </div>
  );
};

interface InchSimulationStageProps {
  simulation: UseInchSimulationResult;
  children: React.ReactNode;
}

/**
 * 1) Cria a tela desejada no iframe.
 * 2) Só então reduz para caber no monitor. Menu fica no portal do body.
 */
export const InchSimulationStage: React.FC<InchSimulationStageProps> = ({
  simulation,
  children,
}) => {
  if (!simulation.isSimulating) {
    return <>{children}</>;
  }

  const { canvasWidth, canvasHeight, inchScale } = simulation;

  return (
    <div className={styles.inchSimulationStage}>
      <SimulatedViewportFrame
        width={canvasWidth}
        height={canvasHeight}
        scale={inchScale}
        title={`Simulação ${canvasWidth}×${canvasHeight}`}
      >
        {children}
      </SimulatedViewportFrame>
    </div>
  );
};
